import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BookPromise } from '../../types';
import { getGraphQLClient, GENERATE_BOOK_PROMISE_SUGGESTIONS } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { Card, Spinner, SuggestionChips } from '../common';

interface BookPromisesPanelProps {
  bookId: string;
  promises: BookPromise[];
  // Live coverage: promise ids that at least one scene plan point delivers.
  linkedPromiseIds: Set<string>;
  onAdd: (content: string) => void;
  onSave: (promiseId: string, content: string) => void;
  onDelete: (promiseId: string) => void;
  onReorder: (orderedIds: string[]) => void;
}

// The book's "ideas" — a low-friction capture inbox for any stray thought about
// the book so it doesn't get lost. Some get placed into a scene (coverage shown
// as a quiet dot), the rest stay floating until you place or drop them. Sits
// above chapters and seeds chapter generation. See docs/book-promises-plan.md.
export default function BookPromisesPanel({
  bookId,
  promises,
  linkedPromiseIds,
  onAdd,
  onSave,
  onDelete,
  onReorder,
}: BookPromisesPanelProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggesting, setSuggesting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const placed = promises.filter((p) => linkedPromiseIds.has(p.id)).length;
  const floating = promises.length - placed;

  // Capture flow: save, clear, and keep focus so ideas can be dumped in a row.
  const addDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setDraft('');
    inputRef.current?.focus();
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const data = await getGraphQLClient().request<{
        generateBookPromiseSuggestions: {
          success: boolean;
          suggestions: string[] | null;
          errors: string[];
        };
      }>(GENERATE_BOOK_PROMISE_SUGGESTIONS, { bookId });
      const result = data.generateBookPromiseSuggestions;
      const have = new Set(promises.map((p) => p.content.trim().toLowerCase()));
      setSuggestions((result.suggestions ?? []).filter((s) => !have.has(s.trim().toLowerCase())));
    } catch (err) {
      captureException(err);
    } finally {
      setSuggesting(false);
    }
  };

  const acceptSuggestion = (content: string) => {
    onAdd(content);
    setSuggestions((prev) => prev.filter((s) => s !== content));
  };

  const summary =
    promises.length === 0
      ? ''
      : floating === 0
        ? t('bookPromises.allPlaced', 'all placed')
        : placed === 0
          ? t('bookPromises.summaryFloating', { floating, defaultValue: '{{floating}} floating' })
          : t('bookPromises.summary', {
              placed,
              floating,
              defaultValue: '{{placed}} placed · {{floating}} floating',
            });

  return (
    <Card padding="md" className="mb-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-800"
        >
          {collapsed ? '▸' : '▾'}
        </button>
        <span className="font-semibold text-gray-800 dark:text-gray-100">
          💡 {t('bookPromises.title', 'Ideas')}
        </span>
        <span className="text-xs text-gray-400">{summary}</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={suggest}
          disabled={suggesting}
          className="shrink-0 text-xs text-indigo-500 hover:text-indigo-700 disabled:opacity-50"
          title={t('bookPromises.suggestHint', 'Let AI suggest ideas from the summary')}
        >
          {suggesting ? <Spinner size="xs" /> : t('bookPromises.suggest', '✨ Suggest')}
        </button>
      </div>

      {!collapsed && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-gray-400">
            {t('bookPromises.subtitle', "Anything you don't want to forget — jot it here.")}
          </p>

          {/* Capture-first: the input lives at the top, always ready. */}
          <div>
            <input
              ref={inputRef}
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDraft();
                }
              }}
              placeholder={t('bookPromises.addPlaceholder', 'Jot a thought…')}
              className="w-full text-sm bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-indigo-400 focus:outline-none py-1"
            />
            <p className="text-[10px] text-gray-300 dark:text-gray-600 mt-0.5">
              {t('bookPromises.autosaved', 'Saved automatically')}
            </p>
          </div>

          {promises.length === 0 && suggestions.length === 0 && (
            <p className="text-xs text-gray-400 leading-relaxed">
              {t(
                'bookPromises.empty',
                'Had a thought about your book? Drop it here — a scene, a line, a “what if”, a worry. Nothing here gets lost; sort it out later.'
              )}
            </p>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              if (!over || active.id === over.id) return;
              const ids = promises.map((p) => p.id);
              const oldIndex = ids.indexOf(String(active.id));
              const newIndex = ids.indexOf(String(over.id));
              if (oldIndex === -1 || newIndex === -1) return;
              onReorder(arrayMove(ids, oldIndex, newIndex));
            }}
          >
            <SortableContext
              items={promises.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-0.5">
                {promises.map((promise) => (
                  <PromiseRow
                    key={promise.id}
                    promise={promise}
                    addressed={linkedPromiseIds.has(promise.id)}
                    onSave={(content) => onSave(promise.id, content)}
                    onDelete={() => onDelete(promise.id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          {suggestions.length > 0 && (
            <SuggestionChips
              suggestions={suggestions}
              isLoading={suggesting}
              onSelect={acceptSuggestion}
              onRefresh={suggest}
            />
          )}
        </div>
      )}
    </Card>
  );
}

function PromiseRow({
  promise,
  addressed,
  onSave,
  onDelete,
}: {
  promise: BookPromise;
  addressed: boolean;
  onSave: (content: string) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(promise.content);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: promise.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== promise.content) onSave(trimmed);
    else setDraft(promise.content);
  };

  return (
    <li ref={setNodeRef} style={style} className="group/pr flex items-center gap-2 text-sm">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing text-[10px] opacity-100 lg:opacity-0 lg:group-hover/pr:opacity-100"
        aria-label={t('chaptersFirst.dragHandle', 'Drag to reorder')}
      >
        ⋮⋮
      </button>
      <span
        className={`shrink-0 w-2 h-2 rounded-full ${
          addressed ? 'bg-emerald-500' : 'border border-gray-300 dark:border-gray-600'
        }`}
        title={
          addressed
            ? t('bookPromises.addressed', 'Placed in a scene')
            : t('bookPromises.notAddressed', 'Still floating')
        }
      />
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            type="text"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                setDraft(promise.content);
                setEditing(false);
              }
            }}
            className="w-full bg-transparent border-b border-indigo-400 focus:outline-none py-0.5"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraft(promise.content);
              setEditing(true);
            }}
            className={`text-left w-full hover:text-indigo-600 dark:hover:text-indigo-300 ${
              addressed ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-200'
            }`}
          >
            {promise.content}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('common.delete', 'Delete')}
        title={t('common.delete', 'Delete')}
        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-100 lg:opacity-0 lg:group-hover/pr:opacity-100 focus:opacity-100 transition"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </li>
  );
}
